import * as XLSX from "xlsx";
import React, { useState } from "react";
import { grey } from "@mui/material/colors";
import InfoIcon from "@mui/icons-material/Info";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import { Box, Paper, Typography, CircularProgress, TextField, Tooltip, IconButton } from "@mui/material";

function ProductSelectionSection({ value, setValue }) {
	const [discountBarcodes, setDiscountBarcodes] = useState("");
	const [nodBarcodes, setNodBarcodes] = useState("");
	const [loadingType, setLoadingType] = useState("");
	const [dragOver, setDragOver] = useState("");

	const handleFileUpload = (file, type) => {
		if (!file) return;

		setLoadingType(type);

		const reader = new FileReader();
		reader.onload = (event) => {
			setTimeout(() => {
				try {
					const data = new Uint8Array(event.target.result);
					const workbook = XLSX.read(data, { type: "array" });
					const worksheet = workbook.Sheets[workbook.SheetNames[0]];
					const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

					const barcodes = jsonData
						.slice(1)
						.map((row) => row[0])
						.filter((barcode) => barcode)
						.slice(0, 1000); // limit for performance

					const barcodeText = barcodes.join("\n");

					// Update state
					setValue((prev) => ({
						...prev,
						product_selection: {
							...prev.product_selection,
							[type === "discount" ? "discount_product" : "nod"]: barcodes,
						},
					}));

					if (type === "discount") setDiscountBarcodes(barcodeText);
					else setNodBarcodes(barcodeText);
				} catch (error) {
					console.error("File processing error:", error);
				} finally {
					setLoadingType("");
				}
			}, 100);
		};

		reader.readAsArrayBuffer(file);
	};

	const handleDrop = (e, type) => {
		e.preventDefault();
		setDragOver("");
		const file = e.dataTransfer.files[0];
		handleFileUpload(file, type);
	};

	const renderUploadSection = (type, valueText, setTextValue, label) => (
		<Paper
			elevation={3}
			sx={{
				p: 2,
				minWidth: 530,
				position: "relative",
				height: 300,
				border: dragOver === type ? "2px dashed #2196f3" : "1px solid #ddd",
				bgcolor: dragOver === type ? "#f0faff" : "white",
				transition: "0.2s",
				overflow: "hidden",
			}}
			onDragOver={(e) => {
				e.preventDefault();
				setDragOver(type);
			}}
			onDragLeave={() => setDragOver("")}
			onDrop={(e) => handleDrop(e, type)}>
			<Typography variant="h6" gutterBottom>
				{label}{" "}
				<Tooltip title="Drag and drop only CSV/Excel files" placement="right-end">
					<IconButton size="small">
						<InfoIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Typography>

			{/* "+" Watermark if empty */}
			{!valueText && loadingType !== type && (
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						color: "#ccc",
						cursor: "pointer",
						userSelect: "none",
						zIndex: 0,
					}}
					onClick={() => document.getElementById(`${type}-upload`).click()}>
					<FileUploadRoundedIcon fontSize="large" sx={{ transform: "scale(1.8)" }} />
				</Box>
			)}

			{/* Hidden file input */}
			<input
				type="file"
				accept=".csv,.xlsx,.xls"
				id={`${type}-upload`}
				hidden
				onChange={(e) => handleFileUpload(e.target.files[0], type)}
			/>

			{/* Textarea */}
			<Box sx={{ mt: 2, position: "relative", zIndex: 1 }}>
				{loadingType === type ? (
					<CircularProgress size={24} />
				) : (
					<Box border={`1px solid ${grey[500]}`}>
						<TextField
							fullWidth
							multiline
							minRows={8}
							variant="outlined"
							value={valueText}
							sx={{
								overflowY: "scroll",
								height: 245,
								border: "0px",
								"& .MuiOutlinedInput-root": {
									"& fieldset": {
										border: "none",
									},
								},
							}}
							onChange={(e) => {
								const text = e.target.value;
								const lines = text.split("\n").filter((line) => line.trim() !== "");

								setTextValue(text);

								setValue((prev) => ({
									...prev,
									product_selection: {
										...prev.product_selection,
										[type === "discount" ? "discount_product" : "nod"]: lines,
									},
								}));
							}}
							placeholder="Enter barcodes one per line..."
						/>
					</Box>
				)}
			</Box>
		</Paper>
	);

	return (
		<Box display="flex" gap={4} justifyContent="center" alignItems="flex-start" mt={2} flexWrap="wrap" width={"95%"}>
			{renderUploadSection("discount", discountBarcodes, setDiscountBarcodes, "Barcode on Discount")}
			{renderUploadSection("nod", nodBarcodes, setNodBarcodes, "NOD Barcodes")}
		</Box>
	);
}

export default ProductSelectionSection;
